import {
  BRAND,
  BRAND_FOUNDER_ID,
  BRAND_ORGANIZATION_ID,
  BRAND_WEBSITE_ID,
} from "./brand";

type MetaTag = React.JSX.IntrinsicElements["meta"];

interface LinkTag {
  rel: string;
  href: string;
  type?: string;
  sizes?: string;
}

interface MetaConfig {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "profile" | "product";
  ogUrl?: string;
  ogLocale?: string;
  twitterCard?: "summary" | "summary_large_image";
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonical?: string;
  noindex?: boolean;
  // Emit `noindex, follow` — keep the page out of the index but let crawlers
  // follow its links (used for thin listing pages below the sitemap threshold).
  // Ignored when `noindex` is set.
  noindexFollow?: boolean;
  keywords?: string[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
}

// Brand social card (1200×630) served from /public. Used whenever a page
// doesn't provide its own og:image so every shared link gets a real preview.
export const DEFAULT_OG_IMAGE = `${BRAND.url}/img/og/default.png`;

export function createMetaTags(config: MetaConfig): {
  meta: MetaTag[];
  links?: LinkTag[];
} {
  const meta: MetaTag[] = [
    { name: "description", content: config.description },
  ];

  // Add keywords if provided
  if (config.keywords?.length) {
    meta.push({ name: "keywords", content: config.keywords.join(", ") });
  }

  // Add author if provided
  if (config.author) {
    meta.push({ name: "author", content: config.author });
  }

  // Robots directive
  if (config.noindex) {
    meta.push({ name: "robots", content: "noindex, nofollow" });
  } else if (config.noindexFollow) {
    meta.push({ name: "robots", content: "noindex, follow" });
  }

  // OpenGraph tags
  meta.push({ property: "og:title", content: config.ogTitle || config.title });
  meta.push({
    property: "og:description",
    content: config.ogDescription || config.description,
  });
  meta.push({ property: "og:type", content: config.ogType || "website" });
  meta.push({ property: "og:site_name", content: BRAND.name });
  meta.push({ property: "og:locale", content: config.ogLocale || "en_US" });

  if (config.ogUrl) {
    meta.push({ property: "og:url", content: config.ogUrl });
  }

  const ogImage = config.ogImage || DEFAULT_OG_IMAGE;
  meta.push({ property: "og:image", content: ogImage });
  meta.push({
    property: "og:image:alt",
    content: config.ogTitle || config.title,
  });

  // Article-specific OpenGraph tags
  if (config.ogType === "article") {
    if (config.author) {
      meta.push({ property: "article:author", content: config.author });
    }
    if (config.publishedTime) {
      meta.push({
        property: "article:published_time",
        content: config.publishedTime,
      });
    }
    if (config.modifiedTime) {
      meta.push({
        property: "article:modified_time",
        content: config.modifiedTime,
      });
    }
    if (config.section) {
      meta.push({ property: "article:section", content: config.section });
    }
    if (config.tags?.length) {
      config.tags.forEach((tag) => {
        meta.push({ property: "article:tag", content: tag });
      });
    }
  }

  // Twitter Card tags
  const twitterCard = config.twitterCard || "summary_large_image";
  meta.push({ name: "twitter:card", content: twitterCard });
  meta.push({
    name: "twitter:title",
    content: config.twitterTitle || config.ogTitle || config.title,
  });
  meta.push({
    name: "twitter:description",
    content:
      config.twitterDescription || config.ogDescription || config.description,
  });

  meta.push({
    name: "twitter:image",
    content: config.twitterImage || ogImage,
  });

  meta.push({ title: config.title });

  const result: { meta: MetaTag[]; links?: LinkTag[] } = {
    meta,
  };

  // Canonical link
  if (config.canonical) {
    result.links = [{ rel: "canonical", href: config.canonical }];
  }

  return result;
}

// Brand-specific meta tag presets
export const createBrandMeta = (
  config: Omit<MetaConfig, "title"> & { title: string },
) =>
  createMetaTags({
    ...config,
    title: config.title.includes("Dream Coach Match")
      ? config.title
      : `${config.title} | Dream Coach Match`,
  });

export const createCoachMeta = (
  coachName: string,
  config: Omit<MetaConfig, "title">,
) =>
  createMetaTags({
    ...config,
    title: `${coachName} | Dream Coach Match`,
    ogType: "profile",
  });

export const createArticleMeta = (
  title: string,
  config: Omit<MetaConfig, "title" | "ogType">,
) =>
  createMetaTags({
    ...config,
    title,
    ogType: "article",
  });

export const createNoIndexMeta = (title: string, description: string) =>
  createMetaTags({
    title,
    description,
    noindex: true,
  });

// Structured data helpers
export function createPersonStructuredData(person: {
  name: string;
  jobTitle?: string;
  image?: string;
  url?: string;
  sameAs?: string[];
  description?: string;
  address?: {
    addressLocality?: string;
    addressRegion?: string;
    addressCountry?: string;
  };
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: person.name,
    ...(person.jobTitle && { jobTitle: person.jobTitle }),
    ...(person.image && { image: person.image }),
    ...(person.url && { url: person.url }),
    ...(person.sameAs && { sameAs: person.sameAs }),
    ...(person.description && { description: person.description }),
    ...(person.address && {
      address: { "@type": "PostalAddress", ...person.address },
    }),
  };
}

export function createArticleStructuredData(article: {
  headline: string;
  description?: string;
  image?: string;
  author?: string;
  publishedDate?: string;
  modifiedDate?: string;
  url?: string;
}) {
  const articleUrl = article.url?.trim();
  const articleId = articleUrl ? `${articleUrl}#article` : undefined;

  return {
    "@context": "https://schema.org",
    "@type": ["Article", "BlogPosting"],
    ...(articleId ? { "@id": articleId } : {}),
    headline: article.headline,
    ...(article.description && { description: article.description }),
    ...(article.image && { image: article.image }),
    ...(article.author && {
      author: {
        "@type": "Person",
        name: article.author,
      },
    }),
    ...(article.publishedDate && { datePublished: article.publishedDate }),
    ...(article.modifiedDate && { dateModified: article.modifiedDate }),
    ...(articleUrl && {
      mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl },
    }),
    ...(articleUrl && { url: articleUrl }),
    inLanguage: "en",
    publisher: {
      "@type": "Organization",
      "@id": BRAND_ORGANIZATION_ID,
      name: BRAND.name,
      url: BRAND.url,
      logo: {
        "@type": "ImageObject",
        url: BRAND.logo,
      },
      address: {
        "@type": "PostalAddress",
        ...BRAND.address,
      },
      telephone: BRAND.telephone,
    },
  };
}

export function createProfilePageStructuredData(page: {
  url: string;
  name: string;
  description?: string;
  image?: string;
  jobTitle?: string;
  sameAs?: string[];
}) {
  const pageUrl = page.url.trim();
  const profileId = `${pageUrl}#profile`;
  const personId = `${pageUrl}#person`;
  const sanitizedSameAs = page.sameAs
    ?.map((value) => value.trim())
    .filter((value) => value.length > 0);
  const uniqueSameAs = sanitizedSameAs
    ? [...new Set(sanitizedSameAs)]
    : undefined;

  const graph = [
    {
      "@type": "ProfilePage",
      "@id": profileId,
      url: pageUrl,
      name: page.name,
      ...(page.description && { description: page.description }),
      inLanguage: "en",
      mainEntity: { "@id": personId },
    },
    {
      "@type": "Person",
      "@id": personId,
      name: page.name,
      url: pageUrl,
      ...(page.jobTitle && { jobTitle: page.jobTitle }),
      ...(page.description && { description: page.description }),
      ...(page.image && { image: page.image }),
      ...(uniqueSameAs && uniqueSameAs.length > 0
        ? { sameAs: uniqueSameAs }
        : {}),
    },
  ] satisfies Array<Record<string, unknown>>;

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}

export function createServiceStructuredData(service: {
  name: string;
  description: string;
  provider: string;
  url?: string;
  price?: string;
  image?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    description: service.description,
    provider: {
      "@type": "Person",
      name: service.provider,
    },
    ...(service.url && { url: service.url }),
    ...(service.price && {
      offers: {
        "@type": "Offer",
        price: service.price,
        priceCurrency: "USD",
      },
    }),
    ...(service.image && { image: service.image }),
  };
}

export function createFaqStructuredData(
  faqs: ReadonlyArray<{ question: string; answer: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function createBreadcrumbStructuredData(
  items: ReadonlyArray<{ name: string; url: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Canonical Organization JSON-LD for Dream Coach Match itself.
 *
 * Use this on every public page where it's worth surfacing the brand
 * Organization. The shared `@id` (BRAND_ORGANIZATION_ID) is the single
 * Knowledge Graph node — every article publisher, breadcrumb item, and
 * service provider that refers to DCM should reference the same id so
 * Google merges signals into one entity rather than fragmenting.
 */
export function createBrandOrganizationStructuredData() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": BRAND_ORGANIZATION_ID,
    name: BRAND.name,
    legalName: BRAND.legalName,
    alternateName: BRAND.alternateName,
    url: BRAND.url,
    logo: { "@type": "ImageObject", url: BRAND.logo },
    image: BRAND.logo,
    description: BRAND.organizationDescription,
    slogan: BRAND.slogan,
    foundingDate: BRAND.foundingDate,
    founder: { "@type": "Person", "@id": BRAND_FOUNDER_ID },
    email: BRAND.email,
    telephone: BRAND.telephone,
    address: {
      "@type": "PostalAddress",
      ...BRAND.address,
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: BRAND.email,
      telephone: BRAND.telephone,
      url: `${BRAND.url}/page/contact`,
      areaServed: "Worldwide",
      availableLanguage: ["English"],
    },
    areaServed: "Worldwide",
    knowsAbout: BRAND.knowsAbout,
    sameAs: BRAND.sameAs,
  };
}

/**
 * Person JSON-LD for one of the brand's people (Yuri, Marcel).
 *
 * Distinct from the generic `createPersonStructuredData` (used for coach
 * profiles and blog authors): every brand person links back to the brand
 * Organization via `worksFor: {@id}`, and carries a stable `@id` anchored to
 * the About page so the Organization's `founder` reference resolves to the
 * same node. Empty/blank `sameAs` entries are dropped so we never ship an
 * unverified profile URL.
 */
export function createBrandPersonStructuredData(person: {
  id: string;
  name: string;
  jobTitle?: string;
  description?: string;
  url?: string;
  image?: string;
  sameAs?: ReadonlyArray<string>;
}) {
  const sameAs = person.sameAs
    ?.map((value) => value.trim())
    .filter((value) => value.length > 0);

  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": person.id,
    name: person.name,
    ...(person.jobTitle && { jobTitle: person.jobTitle }),
    ...(person.description && { description: person.description }),
    ...(person.url && { url: person.url }),
    ...(person.image && { image: person.image }),
    worksFor: { "@id": BRAND_ORGANIZATION_ID },
    ...(sameAs && sameAs.length > 0 ? { sameAs } : {}),
  };
}

/**
 * Canonical WebSite JSON-LD for the dreamcoachmatch.com domain.
 *
 * Linked to the brand Organization via `publisher: {@id}` so the WebSite
 * node attaches to the same Knowledge Graph entity as every article and
 * service provider on the site.
 */
export function createBrandWebsiteStructuredData() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": BRAND_WEBSITE_ID,
    name: BRAND.name,
    alternateName: BRAND.alternateName,
    url: BRAND.url,
    description: BRAND.description,
    inLanguage: "en-US",
    publisher: { "@id": BRAND_ORGANIZATION_ID },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${BRAND.url}/discovery?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export function createOrganizationStructuredData(org: {
  name: string;
  url: string;
  description?: string;
  logo?: string;
  foundedYear?: number;
  founderName?: string;
  addressCountry?: string;
  sameAs?: string[];
}) {
  const sanitizedSameAs = org.sameAs
    ?.map((v) => v.trim())
    .filter((v) => v.length > 0);
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${org.url}#organization`,
    name: org.name,
    url: org.url,
    ...(org.description && { description: org.description }),
    ...(org.logo && {
      logo: { "@type": "ImageObject", url: org.logo },
    }),
    ...(org.foundedYear && { foundingDate: String(org.foundedYear) }),
    ...(org.founderName && {
      founder: { "@type": "Person", name: org.founderName },
    }),
    ...(org.addressCountry && {
      address: {
        "@type": "PostalAddress",
        addressCountry: org.addressCountry,
      },
    }),
    ...(sanitizedSameAs && sanitizedSameAs.length > 0
      ? { sameAs: [...new Set(sanitizedSameAs)] }
      : {}),
  };
}

export function createCourseStructuredData(course: {
  name: string;
  url: string;
  description?: string;
  providerName?: string;
  providerUrl?: string;
  image?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "@id": `${course.url}#course`,
    name: course.name,
    url: course.url,
    ...(course.description && { description: course.description }),
    ...(course.image && { image: course.image }),
    ...(course.providerName && {
      provider: {
        "@type": "Organization",
        name: course.providerName,
        ...(course.providerUrl && { url: course.providerUrl }),
      },
    }),
    inLanguage: "en",
  };
}
